'use client'

import { usePathname, useRouter } from 'next/navigation'
import { startTransition, useEffect, useRef } from 'react'

const ROUTE_TRANSITION_WAIT_MS = 4000
const VIEW_TRANSITION_STYLE_ID = 'witn-framer-view-transition-styles'

const viewTransitionStyles = `
  @media (prefers-reduced-motion) {
    ::view-transition-group(*),
    ::view-transition-old(*),
    ::view-transition-new(*) {
      animation: none !important;
    }
  }

  ::view-transition-old(*),
  ::view-transition-new(*) {
    backface-visibility: hidden;
    mix-blend-mode: normal;
  }

  @property --view-transition-wipe-offset {
    syntax: '<number>';
    initial-value: 0;
    inherits: false;
  }

  @keyframes view-transition-exit {
    0% {
      opacity: 1;
      transform: translate(0px, 0px) scale(1) rotateX(0deg) rotateY(0deg) rotateZ(0deg);
    }

    100% {
      opacity: 1;
      transform: translate(0px, 0px) scale(1) rotateX(0deg) rotateY(0deg) rotateZ(0deg);
    }
  }

  @keyframes view-transition-enter {
    0% {
      opacity: 1;
      transform: translate(0px, 0px) scale(1) rotateX(0deg) rotateY(0deg) rotateZ(0deg);
      --view-transition-wipe-offset: 0;
    }

    100% {
      opacity: 1;
      transform: translate(0px, 0px) scale(1) rotateX(0deg) rotateY(0deg) rotateZ(0deg);
      --view-transition-wipe-offset: 1;
    }
  }

  ::view-transition-old(root) {
    animation-name: view-transition-exit;
    animation-duration: 0.2s;
    animation-delay: 0s;
    animation-timing-function: cubic-bezier(0.27, 0, 0.51, 1);
    animation-fill-mode: both;
  }

  ::view-transition-new(root) {
    animation-name: view-transition-enter;
    animation-duration: 0.4s;
    animation-delay: 0s;
    animation-timing-function: cubic-bezier(0.27, 0, 0.51, 1);
    animation-fill-mode: both;
    mask-image: linear-gradient(
      270deg,
      black calc(calc(0% - 100%) + calc(calc(100% + 100%) * var(--view-transition-wipe-offset))),
      transparent calc(calc(100% + 100%) * var(--view-transition-wipe-offset))
    );
    -webkit-mask-image: linear-gradient(
      270deg,
      black calc(calc(0% - 100%) + calc(calc(100% + 100%) * var(--view-transition-wipe-offset))),
      transparent calc(calc(100% + 100%) * var(--view-transition-wipe-offset))
    );
  }
`

type PendingTransition = {
  pathname: string
  resolve: () => void
  timeout: number
}

function normalizePathname(pathname: string) {
  let end = pathname.length

  while (end > 1 && pathname.charCodeAt(end - 1) === 47) {
    end -= 1
  }

  return end === pathname.length ? pathname : pathname.slice(0, end)
}

function getAnchorFromEvent(event: MouseEvent) {
  if (!(event.target instanceof Element)) return null
  return event.target.closest<HTMLAnchorElement>('a[href]')
}

function isModifiedClick(event: MouseEvent) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
}

function shouldSkipTransition(event: MouseEvent, anchor: HTMLAnchorElement) {
  if (event.defaultPrevented || event.button !== 0 || isModifiedClick(event)) {
    return true
  }

  if (anchor.target && anchor.target !== '_self') return true
  if (anchor.hasAttribute('download')) return true
  if (anchor.hasAttribute('data-no-route-transition')) return true

  return false
}

function getInternalTarget(anchor: HTMLAnchorElement) {
  const targetUrl = new URL(anchor.href)

  if (targetUrl.origin !== window.location.origin) return null

  const currentPathname = normalizePathname(window.location.pathname)
  const targetPathname = normalizePathname(targetUrl.pathname)

  if (targetPathname === currentPathname) return null

  return {
    href: `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`,
    pathname: targetPathname,
  }
}

function injectViewTransitionStyles() {
  const existing = document.getElementById(VIEW_TRANSITION_STYLE_ID)
  if (existing) return existing

  const style = document.createElement('style')
  style.id = VIEW_TRANSITION_STYLE_ID
  style.textContent = viewTransitionStyles
  document.head.appendChild(style)
  return style
}

function pushRoute(router: ReturnType<typeof useRouter>, href: string) {
  router.push(href)
}

export default function RouteTransitions() {
  const pathname = usePathname()
  const router = useRouter()
  const pendingTransitionRef = useRef<PendingTransition | null>(null)

  useEffect(() => {
    const pendingTransition = pendingTransitionRef.current

    if (
      pendingTransition &&
      pendingTransition.pathname === normalizePathname(pathname)
    ) {
      window.clearTimeout(pendingTransition.timeout)
      pendingTransitionRef.current = null
      pendingTransition.resolve()
    }
  }, [pathname])

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    )

    function resolvePendingTransition() {
      const pendingTransition = pendingTransitionRef.current
      if (!pendingTransition) return

      window.clearTimeout(pendingTransition.timeout)
      pendingTransitionRef.current = null
      pendingTransition.resolve()
    }

    function waitForRouteChange(targetPathname: string) {
      return new Promise<void>((resolve) => {
        const timeout = window.setTimeout(
          resolvePendingTransition,
          ROUTE_TRANSITION_WAIT_MS,
        )

        pendingTransitionRef.current = {
          pathname: targetPathname,
          resolve,
          timeout,
        }
      })
    }

    function handleDocumentClick(event: MouseEvent) {
      if (!document.startViewTransition || prefersReducedMotion.matches) {
        return
      }

      const anchor = getAnchorFromEvent(event)
      if (!anchor || shouldSkipTransition(event, anchor)) return

      const target = getInternalTarget(anchor)
      if (!target || pendingTransitionRef.current) return

      event.preventDefault()
      const style = injectViewTransitionStyles()

      const transition = document.startViewTransition(() => {
        const routeChanged = waitForRouteChange(target.pathname)
        startTransition(pushRoute.bind(null, router, target.href))
        return routeChanged
      })

      void transition.finished.then(
        () => {
          style.remove()
        },
        () => {
          style.remove()
        },
      )
    }

    document.addEventListener('click', handleDocumentClick, { capture: true })

    return () => {
      document.removeEventListener('click', handleDocumentClick, {
        capture: true,
      })
      resolvePendingTransition()
      document.getElementById(VIEW_TRANSITION_STYLE_ID)?.remove()
    }
  }, [router])

  return null
}
